/**
 * The workspace shell: the nav, the bar above the page, and whatever the
 * route puts between them. Same construction as EON Genesis 3 and EON Tacit —
 * the shadcn sidebar wearing this product's palette, a sticky topbar carrying
 * the trail, and the route rendered inside a SidebarInset.
 *
 * The nav is the one piece of shadcn chrome we alter: the active row takes a
 * horizontal wash of the field blue and a blue glyph, which is this product's
 * navigation signature rather than shadcn's flat accent fill.
 *
 * Responsive: at md and up the nav is a rail that collapses to icons with the
 * label moving into a tooltip; below that it is an off-canvas sheet opened
 * from the topbar, which is why the trigger is always in the bar.
 */
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  ChevronsUpDown,
  LayoutGrid,
  ListChecks,
  LogOut,
  Moon,
  Settings,
  Sun,
  type LucideIcon,
} from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { api, MOCK_MODE } from '../api';
import { clearAuth, loadAuth } from '../auth/auth';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from '@/components/ui/sidebar';
import { Initials, StatusBadge } from './patterns';
import { href, useRoute, type Route } from '../router';
import { useTheme } from '../theme';

interface NavItem {
  route: Route;
  label: string;
  icon: LucideIcon;
  /** Route names that should light this row up. */
  match: Route['name'][];
}

const NAV_WORK: NavItem[] = [
  { route: { name: 'overview' }, label: 'Overview', icon: LayoutGrid, match: ['overview'] },
  { route: { name: 'live' }, label: 'Live', icon: Activity, match: ['live'] },
  {
    route: { name: 'sessions' },
    label: 'Sessions',
    icon: ListChecks,
    match: ['sessions', 'session', 'replay'],
  },
];

const NAV_SYSTEM: NavItem[] = [
  { route: { name: 'admin', tab: 'users' }, label: 'Admin', icon: Settings, match: ['admin'] },
];

/** The label each route wears in the trail. */
function crumbsFor(route: Route): Array<{ label: string; href?: string }> {
  switch (route.name) {
    case 'live':
      return [{ label: 'Live' }];
    case 'sessions':
      return [{ label: 'Sessions' }];
    case 'session':
      return [
        { label: 'Sessions', href: href({ name: 'sessions' }) },
        { label: 'Session' },
      ];
    case 'replay':
      return [
        { label: 'Sessions', href: href({ name: 'sessions' }) },
        { label: 'Session', href: href({ name: 'session', id: route.id }) },
        { label: 'Replay' },
      ];
    case 'admin':
      return [{ label: 'Admin' }, { label: route.tab[0]!.toUpperCase() + route.tab.slice(1) }];
    default:
      return [{ label: 'Overview' }];
  }
}

function NavGroup({
  label,
  items,
  route,
  badge,
}: {
  label: string;
  items: NavItem[];
  route: Route;
  badge?: (item: NavItem) => ReactNode;
}) {
  return (
    <SidebarGroup className="py-1">
      <SidebarGroupLabel className="px-3 text-label tracking-[0.08em] uppercase text-ink-faint">
        {label}
      </SidebarGroupLabel>
      <SidebarMenu className="gap-0.5">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.match.includes(route.name);
          return (
            <SidebarMenuItem key={item.label}>
              <SidebarMenuButton
                asChild
                isActive={active}
                tooltip={item.label}
                className="h-9 px-3 text-support data-[active=true]:nav-active-surface data-[active=true]:font-semibold data-[active=true]:text-ink data-[active=true]:[&>svg]:text-field group-data-[collapsible=icon]:px-1.5 [&>svg]:size-[18px]"
              >
                <a href={href(item.route)}>
                  <Icon aria-hidden />
                  <span className="truncate">{item.label}</span>
                  {badge?.(item)}
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}

function AppSidebar() {
  const route = useRoute();
  const auth = MOCK_MODE ? null : loadAuth();
  const [, setTheme, resolved] = useTheme();

  // How many sessions are running right now. Cheap, and it answers the
  // question a trainer opens the dashboard with.
  const activeQ = useQuery({
    queryKey: ['sessions', 'active-count'],
    queryFn: () => api.listSessions({ status: 'active', limit: 200 }),
    refetchInterval: 20_000,
  });
  const activeCount = activeQ.data?.sessions.length ?? 0;

  const name = auth?.user.fullName ?? 'Demo viewer';
  const role = auth?.user.role ?? (MOCK_MODE ? 'demo data' : 'no sign-in');

  return (
    <Sidebar collapsible="icon" className="border-line">
      <SidebarHeader className="p-0">
        <SidebarGroup className="py-2">
          <SidebarMenu>
            <SidebarMenuItem>
              <SidebarMenuButton
                asChild
                size="lg"
                className="h-12 px-2 hover:bg-transparent active:bg-transparent group-data-[collapsible=icon]:px-1.5"
              >
                <a href={href({ name: 'overview' })}>
                  <span className="grid size-8 shrink-0 place-items-center rounded-tile bg-field text-on-field">
                    <svg viewBox="0 0 24 24" className="size-[18px]" aria-hidden>
                      <path
                        d="M6 8h12M6 12h8M6 16h12"
                        stroke="currentColor"
                        strokeWidth={2.2}
                        strokeLinecap="round"
                        fill="none"
                      />
                    </svg>
                  </span>
                  <span className="grid min-w-0 leading-tight">
                    <span className="truncate text-subtitle text-ink">EON Field IQ</span>
                    <span className="truncate text-meta text-ink-faint">
                      {MOCK_MODE ? 'Demo data' : 'Trainer dashboard'}
                    </span>
                  </span>
                </a>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroup>
      </SidebarHeader>

      <SidebarContent className="overflow-x-hidden">
        <NavGroup
          label="Work"
          items={NAV_WORK}
          route={route}
          badge={(item) =>
            item.label === 'Live' && activeCount > 0 ? (
              <span className="ml-auto rounded-full bg-field-soft px-1.5 py-0.5 text-[11px] font-semibold text-field group-data-[collapsible=icon]:hidden">
                {activeCount}
              </span>
            ) : null
          }
        />
        <NavGroup label="System" items={NAV_SYSTEM} route={route} />
      </SidebarContent>

      <SidebarFooter className="pt-1">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  className="flex h-12 w-full items-center gap-2 rounded-control px-2 text-left outline-none transition-colors hover:bg-sidebar-accent group-data-[collapsible=icon]:size-9 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:p-0"
                >
                  <Initials name={name} />
                  <span className="grid min-w-0 flex-1 leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate text-support font-semibold text-ink">{name}</span>
                    <span className="truncate text-meta text-ink-faint capitalize">{role}</span>
                  </span>
                  <ChevronsUpDown className="ml-auto size-3.5 shrink-0 text-ink-faint group-data-[collapsible=icon]:hidden" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent side="top" align="start" sideOffset={6} className="min-w-56">
                <DropdownMenuLabel className="text-meta font-normal text-ink-dim">
                  {auth?.user.email ?? 'Not signed in'}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setTheme(resolved === 'dark' ? 'light' : 'dark')}>
                  {resolved === 'dark' ? <Sun /> : <Moon />}
                  {resolved === 'dark' ? 'Light theme' : 'Dark theme'}
                </DropdownMenuItem>
                {auth ? (
                  <DropdownMenuItem
                    onClick={() => {
                      clearAuth();
                      window.location.assign('/');
                    }}
                  >
                    <LogOut />
                    Sign out
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function Topbar() {
  const route = useRoute();
  const crumbs = crumbsFor(route);
  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-line bg-background/85 px-3 backdrop-blur-xl sm:px-4">
      <SidebarTrigger className="-ml-1 shrink-0" />
      <Separator orientation="vertical" className="mr-1 data-[orientation=vertical]:h-4" />
      <Breadcrumb>
        <BreadcrumbList className="text-meta">
          {crumbs.map((c, i) => (
            // The separator is a sibling <li>, never a child of the item:
            // shadcn renders both as list items.
            <Fragment key={`${c.label}-${i}`}>
              {i > 0 ? <BreadcrumbSeparator /> : null}
              <BreadcrumbItem>
                {c.href ? (
                  <BreadcrumbLink href={c.href}>{c.label}</BreadcrumbLink>
                ) : (
                  <BreadcrumbPage>{c.label}</BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </Fragment>
          ))}
        </BreadcrumbList>
      </Breadcrumb>
      {MOCK_MODE ? (
        <div className="ml-auto">
          <StatusBadge tone="retry">Demo</StatusBadge>
        </div>
      ) : null}
    </header>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset className="min-w-0 bg-background">
        <Topbar />
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
}
